import { useRef, useState, DragEvent } from 'react';
import { ArrowLeftIcon, ArrowUpIcon, XIcon } from 'lucide-react';
import { Mission } from '../../data/missions';
import HowToUse from '../HowToUse/HowToUse';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  calculatePayrollJournal,
  PayrollJournalSummary,
} from '../../api/payrollJournalApi';
import { useFileRun } from '../../hooks/useFileRun';
import type { FileResult } from '../../api/files';
import { ProcessingNotice } from '@/components/ui/processing-notice';

const STEPS = [
  'Upload the Employer’s Summary Excel export (required).',
  'Optionally set an output filename, then hit Generate Journal.',
  'The balanced payroll journal downloads automatically and the computed figures are shown below.',
];

interface PayrollJournalPanelProps {
  mission: Mission;
  onBack: () => void;
}

const currency = (value: number | undefined) => 
  value == null
    ? '—'
    : value.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' });

function PayrollJournalPanel({ mission, onBack }: PayrollJournalPanelProps) {
  const [payrollFile, setPayrollFile] = useState<File | null>(null);
  const [outputFilename, setOutputFilename] = useState('Payroll_Journal.xlsx');
  const { status, error, result, fail, run } = useFileRun<FileResult<PayrollJournalSummary>>();

  const payrollInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);

  const handleSubmit = async () => {
    if (!payrollFile) {
      fail('Upload the Employer’s Summary file before generating the journal.');
      return;
    }
    await run(() => calculatePayrollJournal(payrollFile, outputFilename), 'Calculation failed.');
  };

  const summary = result?.summary ?? null;

  return (
    <section
      style={{ borderTopColor: mission.color }}
      className="mx-auto max-w-[700px] rounded-2xl border border-border border-t-[6px] bg-card p-8 shadow-sm"
    >
      <Button
        variant="ghost"
        onClick={onBack}
        style={{ color: mission.color }}
        className="mb-5 h-auto gap-1.5 px-2 py-1.5 text-sm font-semibold hover:bg-transparent"
      >
        <ArrowLeftIcon className="size-5" /> All missions
      </Button>

      <div className="mb-6 flex items-start gap-4">
        <span
          style={{ backgroundColor: `${mission.color}26`, color: mission.color }}
          className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-xl"
        >
          <mission.icon className="h-7 w-7" />
        </span>
        <div>
          <h2 className="mb-1 text-xl font-bold text-foreground">{mission.title}</h2>
          <p className="text-sm text-muted-foreground">{mission.description}</p>
        </div>
      </div>

      <HowToUse steps={STEPS} />

      <div className="mb-5 rounded-lg border border-border bg-muted p-4">
        <p className="mb-2 text-sm font-semibold text-foreground">📝 Expected file format</p>
        <p className="mb-2 text-sm text-muted-foreground">
          The Employer&rsquo;s Summary export (sheet 0, no header row). It must contain:
        </p>
        <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
          <li>
            Period row (e.g., <em>Feb-2026</em>) in the first 5 rows
          </li>
          <li>
            <em>Ryan Henry</em> row — Director&rsquo;s figures
          </li>
          <li>
            <em>Nishi Patel</em> row — Employee&rsquo;s figures
          </li>
        </ul>
        <p className="mb-1 mt-2 text-sm text-muted-foreground">Columns expected:</p>
        <p className="text-sm text-muted-foreground">
          Name, Tax Code, Total Payments, Basic Pay, Hourly Pay, Hours, Employee Pension, Employee
          NIC, Tax Deducted, Net Pay, Employer NIC, Employer Pension
        </p>
      </div>

      <div className="mb-5">
        <Label className="mb-2 text-sm font-semibold text-foreground">
          Employer&rsquo;s Summary file (required)*
        </Label>
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragActive(true);
          }}
          onDragLeave={() => setDragActive(false)}
          onDrop={(e: DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            setDragActive(false);
            const dropped = e.dataTransfer.files && e.dataTransfer.files[0];
            if (dropped) setPayrollFile(dropped);
          }}
          className={`flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed px-6 py-8 text-center transition-colors ${
            dragActive ? 'border-current bg-muted' : 'border-input bg-muted'
          }`}
          style={dragActive ? { borderColor: mission.color } : undefined}
        >
          <Button
            onClick={() => payrollInputRef.current?.click()}
            style={{ backgroundColor: mission.color }}
            className="text-white hover:opacity-90"
          >
            <ArrowUpIcon /> Upload payroll file
          </Button>
          <span className="text-sm text-muted-foreground">or drag and drop a file here</span>
          <span className="text-xs text-muted-foreground">{mission.maxSizeLabel}</span>
          <input
            ref={payrollInputRef}
            type="file"
            accept={mission.accept}
            hidden
            onChange={(e) => {
              const picked = e.target.files && e.target.files[0];
              if (picked) setPayrollFile(picked);
              e.target.value = '';
            }}
          />
        </div>
        {payrollFile && (
          <div className="mt-2 flex items-center justify-between rounded-lg bg-green-50 px-4 py-2.5 text-sm text-green-700">
            <span>📄 {payrollFile.name}</span>
            <button
              type="button"
              onClick={() => setPayrollFile(null)}
              className="text-green-700 hover:text-green-900"
              aria-label="Remove payroll file"
            >
              <XIcon className="size-4" />
            </button>
          </div>
        )}
      </div>

      <div className="mb-6 rounded-lg border border-border bg-muted p-4">
        <Label htmlFor="output-filename" className="mb-1.5">
          Output filename
        </Label>
        <Input
          id="output-filename"
          value={outputFilename}
          onChange={(e) => setOutputFilename(e.target.value)}
          placeholder="Payroll_Journal.xlsx"
        />
      </div>

      <Button
        onClick={handleSubmit}
        disabled={status === 'loading'}
        style={{ backgroundColor: mission.color }}
        className="w-full text-white hover:opacity-90"
      >
        {status === 'loading' ? 'Generating…' : 'Generate Journal'}
      </Button>
      {status === 'loading' && <ProcessingNotice label={'Generating…'} />}

      {status === 'error' && error && (
        <div className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">⚠️ {error}</div>
      )}
      {status === 'success' && result && (
        <div className="mt-4 rounded-lg bg-green-50 px-4 py-3 text-sm text-green-700">
          ✅ {result.filename} downloaded.
        </div>
      )}
      {status === 'idle' && (
        <div className="mt-4 rounded-lg bg-blue-50 px-4 py-3 text-sm text-blue-700">
          📁 Upload an Employer&rsquo;s Summary file above to get started.
        </div>
      )}

      {summary && (
        <div className="mt-5 rounded-lg border border-border bg-muted p-4">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-sm font-semibold text-foreground">
              📊 Journal figures — {summary.period_str}
            </p>
            <span
              className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                summary.balanced ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}
            >
              {summary.balanced ? 'Balanced' : 'Not balanced'}
            </span>
          </div>

          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            <dt className="text-muted-foreground">Date</dt>
            <dd className="text-right font-medium text-foreground">{summary.date_display}</dd>
            <dt className="text-muted-foreground">Total Dr</dt>
            <dd className="text-right font-medium text-foreground">{currency(summary.total_dr)}</dd>
            <dt className="text-muted-foreground">Total Cr</dt>
            <dd className="text-right font-medium text-foreground">{currency(summary.total_cr)}</dd>
            <dt className="text-muted-foreground">Total tax</dt>
            <dd className="text-right font-medium text-foreground">{currency(summary.total_tax)}</dd>
            <dt className="text-muted-foreground">Total EE NIC</dt>
            <dd className="text-right font-medium text-foreground">{currency(summary.total_ee_nic)}</dd>
            <dt className="text-muted-foreground">Total ER NIC</dt>
            <dd className="text-right font-medium text-foreground">{currency(summary.total_er_nic)}</dd>
            <dt className="text-muted-foreground">Total net pay</dt>
            <dd className="text-right font-medium text-foreground">{currency(summary.total_net_pay)}</dd>
          </dl>

          {summary.employees && Object.keys(summary.employees).length > 0 && (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-border text-muted-foreground">
                    <th className="py-1.5 pr-3 font-semibold">Employee</th>
                    <th className="py-1.5 px-3 text-right font-semibold">Gross</th>
                    <th className="py-1.5 px-3 text-right font-semibold">Tax</th>
                    <th className="py-1.5 px-3 text-right font-semibold">EE NIC</th>
                    <th className="py-1.5 px-3 text-right font-semibold">ER NIC</th>
                    <th className="py-1.5 pl-3 text-right font-semibold">Net pay</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(summary.employees).map(([name, fig]) => (
                    <tr key={name} className="border-b border-border/50 text-foreground">
                      <td className="py-1.5 pr-3">{name}</td>
                      <td className="py-1.5 px-3 text-right">{currency(fig.gross)}</td>
                      <td className="py-1.5 px-3 text-right">{currency(fig.tax)}</td>
                      <td className="py-1.5 px-3 text-right">{currency(fig.ee_nic)}</td>
                      <td className="py-1.5 px-3 text-right">{currency(fig.er_nic)}</td>
                      <td className="py-1.5 pl-3 text-right">{currency(fig.net_pay)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export default PayrollJournalPanel;
